import { useTranslation } from 'react-i18next';
import { communityTrpc } from '@org/clients/community';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@org/ui';
import { Loader2 } from 'lucide-react';
import { Link } from '@tanstack/react-router';

interface LikersModalProps {
  postId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LikersModal({ postId, open, onOpenChange }: LikersModalProps) {
  const { t } = useTranslation('community');

  const { data: likers, isLoading } = communityTrpc.likes.listForPost.useQuery(
    { postId },
    { enabled: open }
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('likers.title')}</DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {!isLoading && likers && likers.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {t('likers.empty')}
          </p>
        )}

        {!isLoading && likers && likers.length > 0 && (
          <ul className="space-y-3">
            {likers.map((liker) => {
              const displayName =
                liker.displayName ||
                `${liker.firstName} ${liker.lastName}`.trim();
              const initials =
                (liker.firstName?.[0] ?? '') + (liker.lastName?.[0] ?? '');

              return (
                <li key={liker.userId}>
                  <Link
                    to="/profile/$userId"
                    params={{ userId: liker.userId }}
                    className="flex items-center gap-3 rounded-lg p-2 transition-colors hover:bg-muted/50"
                    onClick={() => onOpenChange(false)}
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarImage
                        src={liker.profileImageUrl ?? undefined}
                        alt={displayName}
                      />
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {initials}
                      </AvatarFallback>
                    </Avatar>

                    <div className="flex flex-1 items-center gap-2 overflow-hidden">
                      <span className="truncate text-sm font-medium text-foreground">
                        {displayName}
                      </span>
                      {liker.isConnection && (
                        <Badge variant="secondary" className="shrink-0 text-xs">
                          {t('likers.friend')}
                        </Badge>
                      )}
                      {liker.communityRole === 'admin' && (
                        <Badge variant="default" className="shrink-0 text-xs">
                          {t('likers.admin')}
                        </Badge>
                      )}
                      {liker.communityRole === 'moderator' && (
                        <Badge variant="outline" className="shrink-0 text-xs">
                          {t('likers.moderator')}
                        </Badge>
                      )}
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
